import { useMemo, useState } from 'react';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Beneficiary, DistrictCaseload, Household, RegistryProfile, VitalFlag } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

const OFFICER_ROLES = ['ASP_REGISTRY_OFFICER', 'DISTRICT_WELFARE_OFFICER', 'ASP_ADMIN'];
const VITAL_FLAG_READ_ROLES = ['ASP_REGISTRY_OFFICER', 'ASP_ADMIN'];

export function Registry() {
  const { persona } = useAuth();
  const canWrite = persona?.roles.some((r) => OFFICER_ROLES.includes(r)) ?? false;
  const canReadFlags = persona?.roles.some((r) => VITAL_FLAG_READ_ROLES.includes(r)) ?? false;

  const [q, setQ] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [caseloadDistrict, setCaseloadDistrict] = useState(persona?.districtId ?? 'beau-vallon');
  const [showRegister, setShowRegister] = useState(false);
  const [showCreateHousehold, setShowCreateHousehold] = useState(false);
  const [openNin, setOpenNin] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: '50' });
    if (q) p.set('q', q);
    if (districtFilter) p.set('districtId', districtFilter);
    return `/api/v1/registry/beneficiaries?${p}`;
  }, [q, districtFilter]);
  const listQ = useApiQuery<{ items: Beneficiary[]; nextCursor: string | null }>(canWrite ? query : null);
  const caseloadQ = useApiQuery<DistrictCaseload>(canWrite ? `/api/v1/registry/districts/${caseloadDistrict}/caseload` : null);
  const flagsQ = useApiQuery<{ items: VitalFlag[] }>(canReadFlags ? '/api/v1/registry/vital-flags' : null);

  if (!canWrite) {
    return (
      <div>
        <PageHeader title="Registry" />
        <ErrorNote message="This view requires the Registry Officer, District Welfare Officer, or ASP Administrator role." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Beneficiary & Household Registry"
        subtitle="One social registry — every ASP touchpoint, NIN-anchored"
        actions={
          <>
            <button className="btn-secondary" onClick={() => setShowCreateHousehold(true)}>
              + Household
            </button>
            <button className="btn-primary" onClick={() => setShowRegister(true)}>
              + Register beneficiary
            </button>
          </>
        }
      />
      {error ? (
        <div className="mb-4">
          <ErrorNote message={error} />
        </div>
      ) : null}

      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <div className="sm:col-span-1">
          <Field label="Caseload district">
            <input className="field-input" value={caseloadDistrict} onChange={(e) => setCaseloadDistrict(e.target.value)} />
          </Field>
        </div>
        {caseloadQ.loading ? (
          <Spinner />
        ) : caseloadQ.error ? null : (
          <>
            <StatCard label="Beneficiaries" value={caseloadQ.data?.beneficiaries ?? 0} />
            <StatCard label="Households" value={caseloadQ.data?.households ?? 0} />
          </>
        )}
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <input className="field-input max-w-xs" placeholder="Search name or NIN…" value={q} onChange={(e) => setQ(e.target.value)} />
        <input className="field-input max-w-xs" placeholder="Filter by district slug…" value={districtFilter} onChange={(e) => setDistrictFilter(e.target.value)} />
      </div>

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No beneficiaries found" hint="Register the first beneficiary for this district." />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Name</th>
                <th className="table-head">NIN</th>
                <th className="table-head">District</th>
                <th className="table-head">Pay method</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((b) => (
                <tr key={b.id} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">{b.fullName}</td>
                  <td className="table-cell text-xs">{b.nin}</td>
                  <td className="table-cell text-xs">{b.districtId.replaceAll('-', ' ')}</td>
                  <td className="table-cell text-xs">{b.payMethod === 'BANK' ? b.bankAccount ?? 'Bank (no acct on file)' : 'District cash'}</td>
                  <td className="table-cell text-right">
                    <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setOpenNin(b.nin)}>
                      View profile
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {canReadFlags ? (
        <div className="mt-6">
          <h2 className="mb-2 text-sm font-bold text-slate-700">Active vital flags</h2>
          <div className="card overflow-x-auto">
            {flagsQ.loading ? (
              <Spinner />
            ) : (flagsQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No active vital flags" hint="Deaths, departures abroad and imprisonments raised by S07 integrity matching appear here." />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">NIN</th>
                    <th className="table-head">Flag</th>
                    <th className="table-head">Source</th>
                    <th className="table-head">Since</th>
                  </tr>
                </thead>
                <tbody>
                  {flagsQ.data!.items.map((f) => (
                    <tr key={f.id}>
                      <td className="table-cell text-xs">{f.nin}</td>
                      <td className="table-cell">
                        <StatusBadge status={f.type} />
                      </td>
                      <td className="table-cell text-xs">{f.source}</td>
                      <td className="table-cell text-xs text-slate-500">{formatDate(f.since)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}

      {showRegister ? (
        <RegisterModal
          onClose={() => setShowRegister(false)}
          onDone={() => {
            setShowRegister(false);
            listQ.reload();
          }}
        />
      ) : null}
      {showCreateHousehold ? (
        <CreateHouseholdModal onClose={() => setShowCreateHousehold(false)} onDone={() => setShowCreateHousehold(false)} />
      ) : null}
      {openNin ? (
        <ProfileModal
          nin={openNin}
          onClose={() => setOpenNin(null)}
          onError={(m) => setError(m)}
        />
      ) : null}
    </div>
  );
}

function RegisterModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({ nin: '', fullName: '', dob: '', districtId: '', bankAccount: '', payMethod: 'BANK' as 'BANK' | 'DISTRICT_CASH' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/registry/beneficiaries', {
        nin: form.nin,
        fullName: form.fullName,
        dob: form.dob,
        districtId: form.districtId,
        bankAccount: form.bankAccount || undefined,
        payMethod: form.payMethod,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Registration failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register beneficiary" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="NIN">
          <input className="field-input" value={form.nin} onChange={(e) => setForm({ ...form, nin: e.target.value })} placeholder="SC680412007" />
        </Field>
        <Field label="Full name">
          <input className="field-input" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date of birth">
            <input className="field-input" type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} />
          </Field>
          <Field label="District (slug)">
            <input className="field-input" value={form.districtId} onChange={(e) => setForm({ ...form, districtId: e.target.value })} placeholder="beau-vallon" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Pay method">
            <select className="field-input" value={form.payMethod} onChange={(e) => setForm({ ...form, payMethod: e.target.value as 'BANK' | 'DISTRICT_CASH' })}>
              <option value="BANK">Bank transfer</option>
              <option value="DISTRICT_CASH">District cash office</option>
            </select>
          </Field>
          {form.payMethod === 'BANK' ? (
            <Field label="Bank account">
              <input className="field-input" value={form.bankAccount} onChange={(e) => setForm({ ...form, bankAccount: e.target.value })} />
            </Field>
          ) : null}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !form.nin || !form.fullName || !form.dob || !form.districtId}>
            {busy ? 'Registering…' : 'Register'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function CreateHouseholdModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [districtId, setDistrictId] = useState('');
  const [address, setAddress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<Household | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const household = await api.post<Household>('/api/v1/registry/households', { districtId, address });
      setCreated(household);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not create household');
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <Modal title="Household created" onClose={onDone}>
        <p className="text-sm text-slate-600">
          Household <strong>{created.id}</strong> created in {created.districtId.replaceAll('-', ' ')}. Add members from a
          beneficiary's profile using this household ID.
        </p>
        <div className="mt-4 flex justify-end">
          <button className="btn-primary" onClick={onDone}>
            Done
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Create household" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="District (slug)">
          <input className="field-input" value={districtId} onChange={(e) => setDistrictId(e.target.value)} placeholder="baie-lazare" />
        </Field>
        <Field label="Address">
          <input className="field-input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Chemin Les Cannelles, Baie Lazare" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !districtId || !address}>
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ProfileModal({ nin, onClose, onError }: { nin: string; onClose: () => void; onError: (m: string) => void }) {
  const profileQ = useApiQuery<RegistryProfile>(`/api/v1/registry/profile/${nin}`);
  const [householdId, setHouseholdId] = useState('');
  const householdQ = useApiQuery<Household>(householdId ? `/api/v1/registry/households/${householdId}` : null);
  const [memberNin, setMemberNin] = useState('');
  const [relation, setRelation] = useState('OTHER');
  const [since, setSince] = useState(() => new Date().toISOString().slice(0, 10));
  const [incomeSource, setIncomeSource] = useState('EMPLOYMENT');
  const [incomeAmount, setIncomeAmount] = useState('');
  const [incomeEvidence, setIncomeEvidence] = useState('');
  const [busy, setBusy] = useState(false);

  const memberships = profileQ.data?.beneficiary.memberships ?? [];

  const addMember = async () => {
    if (!householdId) return;
    setBusy(true);
    try {
      await api.put(`/api/v1/registry/households/${householdId}/members`, { add: [{ nin: memberNin, relation, since }], remove: [] });
      setMemberNin('');
      householdQ.reload();
    } catch (e) {
      onError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not add member');
    } finally {
      setBusy(false);
    }
  };

  const addIncome = async () => {
    if (!householdId || !incomeAmount || !incomeEvidence) return;
    setBusy(true);
    try {
      await api.post(`/api/v1/registry/households/${householdId}/incomes`, {
        source: incomeSource,
        amount: Number.parseFloat(incomeAmount),
        evidenceRef: incomeEvidence,
      });
      setIncomeAmount('');
      setIncomeEvidence('');
      householdQ.reload();
    } catch (e) {
      onError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not add income');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Profile — ${nin}`} onClose={onClose} wide>
      {profileQ.loading ? (
        <Spinner />
      ) : profileQ.error ? (
        <ErrorNote message={profileQ.error} />
      ) : profileQ.data ? (
        <div className="space-y-4">
          <div>
            <div className="flex items-start justify-between gap-4">
              <div className="text-lg font-bold text-slate-900">{profileQ.data.beneficiary.fullName}</div>
              <a
                className="shrink-0 text-xs font-medium text-brand-600 hover:underline"
                href={`https://onegov-command.vercel.app/citizens/${nin}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                View in Citizen 360 ↗
              </a>
            </div>
            <div className="text-xs text-slate-500">
              DOB {formatDate(profileQ.data.beneficiary.dob)} · District {profileQ.data.beneficiary.districtId.replaceAll('-', ' ')} · Pay{' '}
              {profileQ.data.beneficiary.payMethod}
            </div>
          </div>

          {profileQ.data.vitalFlags.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {profileQ.data.vitalFlags.map((f) => (
                <StatusBadge key={f.id} status={f.type} />
              ))}
            </div>
          ) : null}

          <div>
            <div className="field-label">Household membership</div>
            {memberships.length === 0 ? (
              <p className="text-xs text-slate-500">No current household membership on record.</p>
            ) : (
              <ul className="text-xs text-slate-600">
                {memberships.map((m) => (
                  <li key={m.id}>
                    {m.relation} in household{' '}
                    <button className="font-medium text-brand-600 hover:underline" onClick={() => setHouseholdId(m.householdId)}>
                      {m.householdId}
                    </button>{' '}
                    ({m.household.address})
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <Field label="Look up a household by ID">
              <input className="field-input" value={householdId} onChange={(e) => setHouseholdId(e.target.value)} placeholder="household id" />
            </Field>
          </div>

          {householdQ.loading ? (
            <Spinner />
          ) : householdQ.error ? (
            <ErrorNote message={householdQ.error} />
          ) : householdQ.data ? (
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-sm font-semibold">
                Household size {householdQ.data.size} · monthly income {householdQ.data.monthlyIncome}
              </div>
              <ul className="mt-1 text-xs text-slate-600">
                {(householdQ.data.members ?? []).map((m) => (
                  <li key={m.id}>
                    {m.relation} — {m.beneficiary?.fullName ?? m.nin}
                  </li>
                ))}
              </ul>

              <div className="mt-3 grid grid-cols-3 gap-2">
                <input className="field-input col-span-1" placeholder="NIN to add" value={memberNin} onChange={(e) => setMemberNin(e.target.value)} />
                <select className="field-input col-span-1" value={relation} onChange={(e) => setRelation(e.target.value)}>
                  <option value="HEAD">Head</option>
                  <option value="SPOUSE">Spouse</option>
                  <option value="CHILD">Child</option>
                  <option value="PARENT">Parent</option>
                  <option value="OTHER">Other</option>
                </select>
                <input className="field-input col-span-1" type="date" value={since} onChange={(e) => setSince(e.target.value)} />
              </div>
              <button className="btn-secondary mt-2 !py-1 text-xs" onClick={addMember} disabled={busy || !memberNin}>
                Add member
              </button>

              <div className="mt-3 grid grid-cols-3 gap-2">
                <select className="field-input col-span-1" value={incomeSource} onChange={(e) => setIncomeSource(e.target.value)}>
                  <option value="EMPLOYMENT">Employment</option>
                  <option value="SELF_EMPLOYED">Self-employed</option>
                  <option value="PENSION">Pension</option>
                  <option value="REMITTANCE">Remittance</option>
                  <option value="OTHER">Other</option>
                </select>
                <input className="field-input col-span-1" type="number" placeholder="Monthly SCR" value={incomeAmount} onChange={(e) => setIncomeAmount(e.target.value)} />
                <input className="field-input col-span-1" placeholder="Evidence ref" value={incomeEvidence} onChange={(e) => setIncomeEvidence(e.target.value)} />
              </div>
              <button className="btn-secondary mt-2 !py-1 text-xs" onClick={addIncome} disabled={busy || !incomeAmount || !incomeEvidence}>
                Add income declaration
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}
